/**
 * VerticalPager (web) — the TikTok/Shorts-style capsule pager.
 *
 * A real scroll container: pages physically move with the input and snap
 * with `scroll-snap-type: y mandatory`. Input model:
 *
 *  - Touch: native vertical scrolling (browser-driven) with a small gap
 *    between pages; while a drag crosses ~55% of the page step the active
 *    index flips (mid-drag handoff); a release before the threshold lets CSS
 *    snap spring back to the current page.
 *  - Mouse: drag-to-scroll. A window-level pointer listener drives
 *    `scrollTop` while the drag is past a small slop (no pointer capture, so
 *    the transport layer's own recognizer still sees the move and cancels
 *    taps/long-presses — the "real scroll cancels tap recognition" rule).
 *    Snap is disabled for the duration of the drag. On release the fling
 *    velocity joins the displacement in the settle decision
 *    (pagerPolicy.shouldCommit): a fast flick commits the page even below
 *    the 55% threshold; a slow short drag snaps back.
 *  - Trackpad / mouse wheel: one capsule per physical wheel gesture — the
 *    same rule as the arrows/keys and the native pager. A mouse notch (LINE
 *    mode) is one complete step on its own and chains freely; a trackpad
 *    gesture is a dense stream of frames (finger motion + momentum tail),
 *    so it accumulates clear intent and steps exactly ONE capsule, absorbing
 *    the rest of the stream until the gesture ends or reverses. Every step
 *    drives the programmatic-scroll effect, so snap stays enabled and the
 *    container never free-scrolls from the wheel.
 *
 * Page geometry: each page is `pageHeight` tall and the cells carry a small
 * inter-page gap (PAGE_GAP) — the scroll step is pageHeight + gap, so the
 * gap stays visible between pages while dragging AND as a thin separator
 * under the settled page. `scroll-snap-stop: always` (via the page CSS) plus
 * the settle lock guarantee ONE capsule step per gesture.
 *
 * The container never owns the active index — it reports user-driven changes
 * through `onChange`; the parent's `index` prop drives programmatic scrolling
 * (arrows, keyboard, auto-advance). While `disabled` (play mode, quiz,
 * prompt) every scroll input is ignored — but the page chrome stays
 * tappable: the lock blocks gestures and touch panning, not pointer events.
 */
import {useCallback, useEffect, useRef, useState} from 'react'
import {type ReactNode} from 'react'

import {
  EMPTY_WHEEL_GESTURE,
  INTERACTIVE_SELECTOR,
  pageIndexAt,
  pageStep,
  shouldCommit,
  THRESHOLD,
  type WheelGesture,
  wheelGestureStep,
} from '../pagerPolicy'

const IDLE_MS = 150
/** Mouse movement past this before a press becomes a drag (click vs drag). */
const DRAG_SLOP_PX = 6
/** Cap on settle polling (150ms each) before forcing the landing. */
const SETTLE_POLL_MAX = 20
// Page CSS: hidden scrollbar + hard per-cell snap stops (one gesture = one
// capsule step). Kept as a module-level string so the JSX carries no raw
// text children (the lint rule forbids unwrapped strings).
const PAGER_CSS = [
  '.capsule-pager::-webkit-scrollbar{display:none}',
  '.capsule-pager > div{scroll-snap-stop:always}',
].join('\n')

type DragState = {
  armed: boolean
  active: boolean
  pointerId: number
  startY: number
  startTop: number
  startIndex: number
  moved: boolean
  samples: {y: number; t: number}[]
  winUp: ((ev: PointerEvent) => void) | null
}

export function VerticalPager({
  index,
  total,
  disabled,
  onChange,
  pages,
  smooth = true,
}: {
  /** The parent's active page index (0-based within `pages`). */
  index: number
  total: number
  /**
   * Freeze pager input (e.g. play mode, quiz, exit prompt). While disabled
   * wheel over the pager feed itself is swallowed (the container handler
   * backed by a capture-phase document listener scoped to the pager
   * subtree), an in-flight drag/momentum scroll is cancelled at the lock
   * flip, and touch panning is blocked. Overlays OUTSIDE the pager (the ✕
   * prompt, the quiz) keep scrolling their own content — they are siblings
   * of the pager, so their wheel can never reach the feed. The page chrome
   * stays tappable: the lock blocks gestures and scrolling, not pointer
   * events.
   */
  disabled?: boolean
  /** User-driven index change (drag threshold crossed / settled). */
  onChange: (next: number) => void
  /** Page nodes, sorted by index, each with a stable key. */
  pages: ReactNode[]
  /** Animate programmatic page changes (reduced-motion users get jumps). */
  smooth?: boolean
}) {
  const containerRef = useRef<HTMLDivElement>(null)
  const [pageHeight, setPageHeight] = useState(0)

  // Refs keep the scroll bookkeeping off the React render path.
  const stateRef = useRef({
    index: index,
    settling: false,
    idleTimer: null as ReturnType<typeof setTimeout> | null,
    lastCommitAt: 0,
  })
  const propsRef = useRef({index, total, disabled, onChange, smooth})
  useEffect(() => {
    propsRef.current = {index, total, disabled, onChange, smooth}
  })

  const dragRef = useRef<DragState>({
    armed: false,
    active: false,
    pointerId: -1,
    startY: 0,
    startTop: 0,
    startIndex: 0,
    moved: false,
    samples: [],
    winUp: null,
  })
  const snapDisabledRef = useRef(false)
  const settlePollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  // Wheel-gesture state: which stream we're in, its accumulated intent, and
  // whether this gesture already stepped (one capsule per physical swipe).
  const wheelGestureRef = useRef<WheelGesture>(EMPTY_WHEEL_GESTURE)

  const ph = () => {
    const el = containerRef.current
    return el ? el.clientHeight : 0
  }

  /** Full scroll step: one page plus its trailing gap. */
  const step = () => pageStep(ph())

  const setSnapDisabled = useCallback((disabledSnap: boolean) => {
    snapDisabledRef.current = disabledSnap
    const el = containerRef.current
    if (el) el.style.scrollSnapType = disabledSnap ? 'none' : 'y mandatory'
  }, [])

  const clearIdle = () => {
    const s = stateRef.current
    if (s.idleTimer) {
      clearTimeout(s.idleTimer)
      s.idleTimer = null
    }
  }

  const commit = useCallback((next: number) => {
    const s = stateRef.current
    const {total: totalCount, disabled: isDisabled, onChange: report} =
      propsRef.current
    if (isDisabled || next === s.index) return
    const clamped = Math.max(0, Math.min(totalCount - 1, next))
    if (clamped === s.index) return
    s.index = clamped
    s.settling = true
    s.lastCommitAt = Date.now()
    report(clamped)
  }, [])

  const idleCheck = useCallback(() => {
    const el = containerRef.current
    const s = stateRef.current
    if (!el) return
    const st = step()
    if (st <= 0) return
    const target = pageIndexAt(el.scrollTop, ph(), propsRef.current.total)
    const settled = Math.abs(el.scrollTop - target * st) < 2
    if (settled) s.settling = false
    if (settled && target !== s.index && !propsRef.current.disabled) {
      s.index = target
      propsRef.current.onChange(target)
    }
  }, [])

  const scheduleIdle = useCallback(() => {
    const s = stateRef.current
    clearIdle()
    s.idleTimer = setTimeout(idleCheck, IDLE_MS)
  }, [clearIdle, idleCheck])

  // Continuous scroll tracking: mid-drag threshold handoff + idle settle.
  // The mid-scrub handoff applies to drags (and native touch scrolls); the
  // wheel steps through commits, whose programmatic glide is suppressed here
  // by the settling flag so a step can never cascade into a second commit.
  useEffect(() => {
    const el = containerRef.current
    if (!el) return
    let raf = 0
    const onScroll = () => {
      cancelAnimationFrame(raf)
      raf = requestAnimationFrame(() => {
        const st = step()
        if (st <= 0) return
        scheduleIdle()
        const s = stateRef.current
        if (s.settling || propsRef.current.disabled) return
        const f = el.scrollTop / st
        const floor = Math.floor(f)
        const frac = f - floor
        if (frac >= THRESHOLD) {
          commit(floor + 1)
        } else if (frac <= 1 - THRESHOLD && floor >= 1) {
          commit(floor)
        }
      })
    }
    el.addEventListener('scroll', onScroll, {passive: true})
    el.addEventListener('scrollend', idleCheck as EventListener)
    return () => {
      cancelAnimationFrame(raf)
      el.removeEventListener('scroll', onScroll)
      el.removeEventListener('scrollend', idleCheck as EventListener)
      clearIdle()
    }
  }, [clearIdle, commit, idleCheck, scheduleIdle])

  // Size pages to the viewport (responsive resize included).
  useEffect(() => {
    const el = containerRef.current
    if (!el) return
    const measure = () => setPageHeight(el.clientHeight)
    measure()
    const ro = new ResizeObserver(measure)
    ro.observe(el)
    window.addEventListener('resize', measure)
    return () => {
      ro.disconnect()
      window.removeEventListener('resize', measure)
    }
  }, [])

  // Programmatic navigation (arrows / keys / auto-advance / index resolution):
  // move the container when the parent index no longer matches where we rest.
  useEffect(() => {
    const el = containerRef.current
    if (!el || pageHeight <= 0) return
    const s = stateRef.current
    const targetTop = index * pageStep(pageHeight)
    if (Math.abs(el.scrollTop - targetTop) < 2) return
    s.index = index
    const far = Math.abs(el.scrollTop - targetTop) > pageHeight * 1.5
    el.scrollTo({top: targetTop, behavior: far || !smooth ? 'auto' : 'smooth'})
  }, [index, pageHeight, smooth])

  // A release always lands ON a page boundary, animated like the arrows:
  // snap stays disabled while the page glides into place and is re-enabled
  // only once the scroll actually landed. Smooth scrolling isn't available
  // everywhere (e.g. headless browsers never animate it) — the poll detects
  // a stalled animation and finishes the jump instantly instead of leaving
  // the pager free of snap or mismatched with the committed index.
  const landOn = useCallback(
    (targetTop: number) => {
      const el = containerRef.current
      if (!el) return
      if (!propsRef.current.smooth || Math.abs(el.scrollTop - targetTop) < 2) {
        el.scrollTo({top: targetTop, behavior: 'auto'})
        setSnapDisabled(false)
        return
      }
      el.scrollTo({top: targetTop, behavior: 'smooth'})
      const pollSettle = (iter: number) => {
        if (iter > SETTLE_POLL_MAX) {
          const el3 = containerRef.current
          if (el3) el3.scrollTo({top: targetTop, behavior: 'auto'})
          setSnapDisabled(false)
          return
        }
        const el2 = containerRef.current
        if (!el2) return
        if (Math.abs(el2.scrollTop - targetTop) < 2) {
          setSnapDisabled(false)
          return
        }
        const lastTop = el2.scrollTop
        settlePollTimerRef.current = setTimeout(() => {
          const el3 = containerRef.current
          if (!el3) return
          if (Math.abs(el3.scrollTop - targetTop) < 2) {
            setSnapDisabled(false)
          } else if (Math.abs(el3.scrollTop - lastTop) < 1) {
            // Stalled (smooth unavailable) — land the jump directly.
            el3.scrollTo({top: targetTop, behavior: 'auto'})
            setSnapDisabled(false)
          } else {
            pollSettle(iter + 1)
          }
        }, 150)
      }
      pollSettle(0)
    },
    [setSnapDisabled],
  )

  // Mouse drag-to-scroll. The container's pointer handlers drive scrollTop
  // (no pointer capture, so the transport layer's recognizer keeps receiving
  // its own events and cancels taps/long-presses). A window-level pointerup
  // safety ends the drag even if the button is released outside the
  // container — otherwise snap would stay disabled indefinitely.
  const endDrag = useCallback(
    (velocity: number) => {
      const el = containerRef.current
      const d = dragRef.current
      if (!el || !d.active) return
      d.active = false
      d.armed = false
      el.style.userSelect = ''
      const st = step()
      if (st <= 0) {
        setSnapDisabled(false)
        return
      }
      const s = stateRef.current
      // The landing page: the mid-drag threshold commit if one happened;
      // otherwise decide from the release position + fling velocity (±1 from
      // where the drag began — a fast flick commits even below the 55%
      // threshold, a slow short drag snaps back to the same page).
      let target = s.index
      if (s.index === d.startIndex) {
        const ratio = (el.scrollTop - s.index * st) / st
        const dir = shouldCommit(ratio, velocity)
        target = Math.max(
          0,
          Math.min(propsRef.current.total - 1, s.index + dir),
        )
      }
      if (target !== s.index) {
        commit(target)
      }
      // Animated landing — same glide as the arrows (snap re-enables only
      // once the scroll rests exactly on the page boundary).
      landOn(target * st)
    },
    [commit, landOn, setSnapDisabled],
  )

  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    const el = containerRef.current
    if (!el) return
    if (propsRef.current.disabled) return
    if (e.pointerType !== 'mouse' || e.button !== 0) return
    const target = e.target as HTMLElement | null
    if (target?.closest?.(INTERACTIVE_SELECTOR)) return
    const d = dragRef.current
    d.armed = true
    d.active = false
    d.moved = false
    d.pointerId = e.pointerId
    d.startY = e.clientY
    d.startTop = el.scrollTop
    d.startIndex = stateRef.current.index
    d.samples = [{y: e.clientY, t: performance.now()}]
    // Safety: end the drag if the button is released outside the container.
    const onWinUp = (ev: PointerEvent) => {
      window.removeEventListener('pointerup', onWinUp)
      window.removeEventListener('pointercancel', onWinUp)
      finishDrag(ev.pointerId)
    }
    d.winUp = onWinUp
    window.addEventListener('pointerup', onWinUp)
    window.addEventListener('pointercancel', onWinUp)
  }

  // Compute the release velocity from the last two movement samples and end
  // the drag (idempotent — the window safety and the container handler can
  // both call it; the armed/active guards make the second a no-op).
  const finishDrag = (pointerId: number) => {
    const d = dragRef.current
    if (!d.armed || pointerId !== d.pointerId) return
    d.armed = false
    if (d.winUp) {
      window.removeEventListener('pointerup', d.winUp)
      window.removeEventListener('pointercancel', d.winUp)
      d.winUp = null
    }
    if (!d.moved) return // plain click — the transport layer handles it
    let velocity = 0
    if (d.samples.length >= 2) {
      const a = d.samples[d.samples.length - 2]
      const b = d.samples[d.samples.length - 1]
      const dt = b.t - a.t
      if (dt > 0) velocity = ((a.y - b.y) / dt) * 1000 // up → + (next)
    }
    endDrag(velocity)
  }

  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const el = containerRef.current
    const d = dragRef.current
    if (!el || !d.armed || e.pointerId !== d.pointerId) return
    const dy = e.clientY - d.startY
    if (!d.moved && Math.abs(dy) < DRAG_SLOP_PX) return
    if (!d.moved) {
      d.moved = true
      d.active = true
      setSnapDisabled(true)
      el.style.userSelect = 'none'
    }
    d.samples.push({y: e.clientY, t: performance.now()})
    if (d.samples.length > 4) d.samples.shift()
    el.scrollTop = d.startTop - dy
  }

  const onPointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    finishDrag(e.pointerId)
  }

  // Wheel (trackpad + mouse wheel): ONE capsule per physical wheel gesture,
  // exactly like the arrows/keys (pagerPolicy.wheelGestureStep owns the
  // rule). A mouse notch chains freely; a trackpad swipe — a dense stream of
  // frames with a momentum tail — steps once and absorbs the rest of the
  // gesture, so one two-finger scroll can never jump two capsules. The
  // committed index drives the programmatic-scroll effect (no free scroll,
  // snap stays enabled). While locked (disabled) the wheel is swallowed so
  // nothing can move the feed.
  useEffect(() => {
    const el = containerRef.current
    if (!el) return
    const onWheel = (e: WheelEvent) => {
      if (propsRef.current.disabled) {
        // Locked (play mode / quiz / prompt): swallow the wheel so the
        // browser's native scroll action can't move the feed to another
        // capsule — preventDefault cancels the default scroll.
        e.preventDefault()
        return
      }
      let node: HTMLElement | null = e.target as HTMLElement
      while (node && node !== el) {
        const style = window.getComputedStyle(node)
        const canScroll =
          (style.overflowY === 'auto' || style.overflowY === 'scroll') &&
          node.scrollHeight > node.clientHeight + 1
        if (canScroll) return // inner scrollable owns the wheel
        node = node.parentElement
      }
      // Every frame reaches the gesture reducer (accumulated frames included)
      // and is swallowed, so the container's native scroll can never fight
      // the snap. The reducer decides when the gesture steps one capsule.
      e.preventDefault()
      const now = Date.now()
      const {next: gesture, step} = wheelGestureStep(
        wheelGestureRef.current,
        e.deltaY,
        e.deltaMode,
        now,
      )
      wheelGestureRef.current = gesture
      if (step !== 0) commit(stateRef.current.index + step)
    }
    el.addEventListener('wheel', onWheel, {passive: false})
    return () => {
      el.removeEventListener('wheel', onWheel)
    }
  }, [commit])

  // Lock hardening: while disabled, back the container's own wheel handler
  // with a capture-phase document listener so wheel targeting the feed (or
  // any descendant) is swallowed even if the container handler is bypassed.
  // The listener is SCOPED to the pager subtree: wheel over overlays outside
  // the pager (the ✕ prompt, the quiz) is left alone so their own scrollables
  // keep working — those surfaces are siblings of the pager and can never
  // scroll the feed (no ancestor chain). An in-flight drag or momentum scroll
  // (started before the lock) is cancelled at the flip so it can't keep
  // driving scrollTop after Play/quiz/prompt opened.
  useEffect(() => {
    if (!disabled) return
    const el = containerRef.current
    if (el) {
      // Cancel in-flight momentum / smooth scrolling (a programmatic scroll
      // stops the current scroll in every engine).
      el.scrollTo({top: el.scrollTop, behavior: 'auto'})
      // Abort an active drag: pointermove must not keep moving the feed.
      const d = dragRef.current
      if (d.armed || d.active) {
        d.armed = false
        d.active = false
        el.style.userSelect = ''
        if (d.winUp) {
          window.removeEventListener('pointerup', d.winUp)
          window.removeEventListener('pointercancel', d.winUp)
          d.winUp = null
        }
      }
    }
    const onDocWheel = (e: WheelEvent) => {
      const container = containerRef.current
      if (!container || !(e.target instanceof Node)) return
      if (container.contains(e.target)) e.preventDefault()
    }
    document.addEventListener('wheel', onDocWheel, {
      capture: true,
      passive: false,
    })
    return () => {
      document.removeEventListener('wheel', onDocWheel, {capture: true})
    }
  }, [disabled])

  // Cleanup drag bookkeeping + timers on unmount (pager navigation, X-close).
  useEffect(
    () => () => {
      const d = dragRef.current
      if (d.winUp) {
        window.removeEventListener('pointerup', d.winUp)
        window.removeEventListener('pointercancel', d.winUp)
        d.winUp = null
      }
      if (settlePollTimerRef.current) clearTimeout(settlePollTimerRef.current)
    },
    [],
  )

  const cells = pages.map((node, i) => (
    <div
      key={(node as {key?: string | number}).key ?? i}
      style={{
        // Cell = one page + its gap: snap positions land on i * step, keeping
        // the gap visible between adjacent pages and under the settled page.
        height: pageStep(pageHeight),
        minHeight: pageStep(pageHeight),
        flexShrink: 0,
        scrollSnapAlign: 'start',
        position: 'relative',
        overflow: 'hidden',
        width: '100%',
        backgroundColor: '#000',
      }}>
      <div
        style={{
          height: pageHeight,
          minHeight: pageHeight,
          position: 'relative',
          overflow: 'hidden',
          width: '100%',
        }}>
        {node}
      </div>
    </div>
  ))

  return (
    <>
      <style>{PAGER_CSS}</style>
      <div
        ref={containerRef}
        className="capsule-pager"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={e => finishDrag(e.pointerId)}
        style={{
          height: '100%',
          width: '100%',
          overflowY: 'auto',
          overflowX: 'hidden',
          scrollSnapType: 'y mandatory',
          scrollbarWidth: 'none',
          overscrollBehavior: 'contain',
          // Locked: block touch panning so the page can't scroll; taps on
          // the page chrome still land (gestures are blocked, not events).
          touchAction: disabled ? 'none' : 'pan-y',
        }}>
        {cells}
      </div>
    </>
  )
}